import React from 'react';
import * as Linking from 'expo-linking';
import usePayments from '../../../../../hooks/api/usePayments';
import { getIntl } from '../../../../../utils/intl';
import usePreRedirectArgs from '../../../../../hooks/usePreRedirectArgs';
import AccountHeader from '../../../../shared/Account/AccountHeader';
import { useLoadedUser } from '../../../../../hooks/api/useUser';
import { entitlements } from '../../../../../utils/sharedMessages';
import { isPresent } from 'ts-is-present';
import getDateString from '../../../../../utils/getDateString';
import { getClientType } from '../../../../../hooks/useClientType';
import { ButtonPromptRegistrationCodeConfiguration } from '../../../../shared/RegistrationCode';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import Stack, { ContentStack } from '../../../../v2BaseComponents/Stack';
import TitledSection from '../../../../v2BaseComponents/TitledSection';
import InfoIcon from '../../../../v2BaseComponents/InfoIcon';
import LabelValueDisplay from '../../../../v2BaseComponents/LabelValueDisplay';
import ListItem from '../../../../v2BaseComponents/ListItem';
import Text from '../../../../v2BaseComponents/Text';
import { Platform } from 'react-native';
import { useNestedNavigation } from '../../../../../utils/appNavigationNames';

const intl = getIntl('myAccount.subscriptionDetails', {
    title: 'My Purchases',
    managePurchase: 'Manage Plan',
    planNowActive: '{planName} is Now Active',
    organize6Profiles: 'Organize up to 6 Medical Profiles',
    organize1Profile: 'Organize 1 Medical Profile',
    shareAccess: 'Share access with your Care Team',
    setupAndTraining: 'Received 2 hours of training & setup a $299 value',
    subscriptionBenefits: 'Subscription Benefits',
    startDate: 'Start Date',
    nextPaymentDate: 'Next Payment Date',
    nextPaymentAmount: 'Next Payment Amount',
    planDetails: 'Plan Details',
    planName: 'Plan Name',
    delayingForWebhookPropagation: 'Waiting for updates to process',
});

export type SubscriptionDetailsOwnProps = {};

export type SubscriptionDetailsProps = SubscriptionDetailsOwnProps;

const SubscriptionDetails: React.FC<SubscriptionDetailsProps> = () => {
    const { setPreRedirectArgs } = usePreRedirectArgs();
    const { user } = useLoadedUser({});
    const { nestedAccountNavigation } = useNestedNavigation();
    const {
        paymentStatus,
        isLoadingWebProducts,
        isLoadingBillingPortalUrl,
        createWebBillingPortalSession: createBillingPortalSession,
        myRegistrationCodeStatus,
        isLoadingRegistrationCodeStatus,
        isDelayingForNativeWebhookPropagation,
        isLoadingNativeProductManagementSession,
        createNativeProductManagementSession,
    } = usePayments({
        onCompletedNativeProductManagementSession: () => {
            setPreRedirectArgs(
                {
                    type: 'stripe',
                },
                () => nestedAccountNavigation('redirect', undefined),
            );
        },
        onReceiveBillingPortalSessionUrl: (url) => {
            setPreRedirectArgs(
                {
                    type: 'stripe',
                },
                () => {
                    Linking.openURL(url);
                },
            );
        },
    });

    const labelValues = paymentStatus
        ? [
              {
                  label: intl('startDate'),
                  value: getDateString(paymentStatus.lastPaymentTimestamp || paymentStatus.redemptionTimestamp, 'date'),
              },
              paymentStatus.nextPaymentTimestamp
                  ? {
                        label: intl('nextPaymentDate'),
                        value: getDateString(paymentStatus.nextPaymentTimestamp, 'date'),
                    }
                  : null,
          ].filter(isPresent)
        : [];

    return (
        <AnimateLoadedContent
            isLoading={
                isLoadingBillingPortalUrl ||
                isLoadingWebProducts ||
                !paymentStatus ||
                isLoadingRegistrationCodeStatus ||
                isDelayingForNativeWebhookPropagation ||
                isLoadingNativeProductManagementSession
            }
            message={isDelayingForNativeWebhookPropagation ? intl('delayingForWebhookPropagation') : undefined}
        >
            {() => (
                <>
                    <AccountHeader user={user} />
                    <ActionButtonLayout
                        whiteBackground
                        scrollable
                        actions={[
                            {
                                title: intl('managePurchase'),
                                icon: 'Settings',
                                isDisabled: isLoadingBillingPortalUrl,
                                onPress: () => {
                                    Platform.OS === 'web'
                                        ? createBillingPortalSession({
                                              client: getClientType() as any,
                                          })
                                        : createNativeProductManagementSession();
                                },
                                show: myRegistrationCodeStatus?.status?.type !== 'entitlement',
                            },
                        ]}
                    >
                        <MainContentContainer>
                            <ContentStack>
                                <Stack horizontal fullWidth justify="center">
                                    <InfoIcon
                                        alertIconProps={{
                                            variant: 'success',
                                            name: 'Check',
                                        }}
                                        title={intl('planNowActive', {
                                            planName: entitlements('Family Plan'),
                                        })}
                                    />
                                </Stack>
                                <TitledSection title={intl('planDetails')}>
                                    <ContentStack>
                                        {myRegistrationCodeStatus &&
                                        myRegistrationCodeStatus.status.type !== 'no-registration-code' ? (
                                            <ButtonPromptRegistrationCodeConfiguration showMoreDetails={false} />
                                        ) : null}
                                        <ContentStack>
                                            <LabelValueDisplay
                                                items={[
                                                    {
                                                        label: intl('planName'),
                                                        value: entitlements('Family Plan'),
                                                    },
                                                    ...labelValues,
                                                ]}
                                            />
                                        </ContentStack>
                                        <Stack vertical fullWidth align="flex-start">
                                            <Text variant="body-1">{intl('subscriptionBenefits')}</Text>
                                            <Stack vertical fullWidth>
                                                {(
                                                    [
                                                        'shareAccess',
                                                        paymentStatus!.entitlements.includes('Setup and Training')
                                                            ? 'setupAndTraining'
                                                            : null,
                                                    ] as const
                                                )
                                                    .filter(isPresent)
                                                    .map((_) => (
                                                        <ListItem unordered key={_} variant="body-2">
                                                            {intl(_)}
                                                        </ListItem>
                                                    ))}
                                            </Stack>
                                        </Stack>
                                    </ContentStack>
                                </TitledSection>
                            </ContentStack>
                        </MainContentContainer>
                    </ActionButtonLayout>
                </>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(SubscriptionDetails);
